import {
	createFileRoute,
	useCanGoBack,
	useNavigate,
	useRouteContext,
	useRouter,
} from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import {
	EmailTemplateForm,
	type EmailTemplateFormSchemaValues,
} from "@/forms/email-template-form";
import type { templatesNovoCreateMutation } from "./__generated__/templatesNovoCreateMutation.graphql";
import type { templatesNovoQuery } from "./__generated__/templatesNovoQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(standalone)/templates/novo",
)({
	component: RouteComponent,
});

function TemplateSkeleton() {
	return (
		<div className="h-screen flex flex-col">
			<header className="flex items-center justify-between h-14 px-4 border-b">
				<div className="flex items-center gap-2">
					<Skeleton className="h-4 w-20" />
					<span className="text-muted-foreground">/</span>
					<Skeleton className="h-4 w-32" />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-8 w-20" />
					<Skeleton className="h-8 w-20" />
				</div>
			</header>
			<div className="flex-1 flex">
				<div className="w-80 border-r p-4 space-y-4">
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</div>
				<div className="flex-1 p-4">
					<Skeleton className="h-full w-full" />
				</div>
			</div>
		</div>
	);
}

function TemplateContent() {
	const router = useRouter();
	const canGoBack = useCanGoBack();
	const navigate = useNavigate({ from: Route.fullPath });
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<templatesNovoQuery>(
		graphql`
			query templatesNovoQuery($club: String!) {
				club(slug: $club) {
					id
					emailDomains {
						domain
						enabled
						isDefault
					}
				}
			}
		`,
		{ club: club.slug },
	);

	const [createTemplate, isPending] =
		useMutation<templatesNovoCreateMutation>(graphql`
			mutation templatesNovoCreateMutation(
				$club: String!
				$payload: CreateTemplateInput!
			) {
				createTemplate(club: $club, createTemplateInput: $payload) {
					node {
						id
						title
						subject
						content
						from
						fromName
						replyTo
					}
				}
			}
		`);

	const emailDomains = useMemo(
		() =>
			data.club.emailDomains.map((d) => ({
				domain: d.domain,
				enabled: d.enabled,
				isDefault: d.isDefault,
			})),
		[data.club.emailDomains],
	);

	const onSubmit = (value: EmailTemplateFormSchemaValues) => {
		return createTemplate({
			variables: {
				club: club.slug,
				payload: {
					title: value.title,
					subject: value.subject,
					content: JSON.stringify(value.content),
					from: value.from,
					fromName: value.fromName || undefined,
					replyTo: value.replyTo || undefined,
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Template criado com sucesso!");

				if (canGoBack) {
					router.history.back();
				} else {
					navigate({ to: "/c/$club/automacoes/templates-de-email" });
				}
			},
			onError: (error) => {
				console.error("Error creating template:", error);
				toast.error(
					"Ocorreu um erro ao criar o template. Verifique os dados e tente novamente.",
				);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);
				if (clubRecord) {
					clubRecord.invalidateRecord();
				}
			},
		});
	};

	return (
		<div className="h-screen">
			<EmailTemplateForm
				onSubmit={onSubmit}
				isSubmitting={isPending}
				emailDomains={emailDomains}
			/>
		</div>
	);
}

function RouteComponent() {
	return (
		<Suspense fallback={<TemplateSkeleton />}>
			<TemplateContent />
		</Suspense>
	);
}
